// src/components/TalentModal.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../css/TalentModal.css";
import { useCredits } from "../components/CreditsContext";
// ⬇️ Adjust the path to where your role.js actually lives
import roles from "../data/role"; // e.g. "../data/role" or "../role"

/**
 * Normalizes role.js entries into a single "family" of tiers for a given role name.
 * role.js exports an array where each object already represents a tier (Tier 1/2/3) of the same role.
 */
function buildRoleFamily(roleName) {
  const group = Array.isArray(roles)
    ? roles.filter((r) => String(r.role).toLowerCase() === String(roleName).toLowerCase())
    : [];

  // Map avatars by tier label ("Tier 1"|"Tier 2"|"Tier 3")
  const avatars = {};
  const tiers = [];
  for (const r of group) {
    const lvl = String(r.talentLevel || "").trim(); // e.g. "Tier 1"
    if (lvl && !tiers.includes(lvl)) tiers.push(lvl);
    if (lvl && r.avatars) avatars[lvl] = r.avatars;
  }

  // Sort tiers in logical order Tier 1 -> Tier 3
  tiers.sort((a, b) => {
    const na = parseInt(a.replace(/[^0-9]/g, ""), 10) || 0;
    const nb = parseInt(b.replace(/[^0-9]/g, ""), 10) || 0;
    return na - nb;
  });

  return { group, avatars, tiers };
}

export default function TalentModal({
  talent,           // Expecting at least: { role, talentId } OR a role.js entry
  onClose,
  currentIndex,     // kept for API compatibility
  setCurrentIndex,  // kept for API compatibility
  total = 1,
}) {
  const navigate = useNavigate();
  const { talentRolePass, isTalentUnlocked, unlockTalent } = useCredits();

  // --- Hooks (must run on every render, unconditionally) ---
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const roleName = useMemo(() => talent?.role || talent?.name || "", [talent]);
  const family = useMemo(() => buildRoleFamily(roleName), [roleName]);

  const availableTiers = useMemo(() => {
    if (family.tiers?.length) return family.tiers; // ["Tier 1","Tier 2","Tier 3"]
    const fromProp = Array.isArray(talent?.talentLevelAvailable)
      ? talent.talentLevelAvailable
      : Array.isArray(talent?.levelsAvailable)
      ? talent.levelsAvailable
      : ["Tier 1", "Tier 2", "Tier 3"];
    // Normalize to "Tier N"
    return fromProp
      .map((t) => {
        const s = String(t || "").toLowerCase();
        if (s.startsWith("tier 1") || s.startsWith("entry")) return "Tier 1";
        if (s.startsWith("tier 2") || s.startsWith("mid")) return "Tier 2";
        if (s.startsWith("tier 3") || s.startsWith("senior")) return "Tier 3";
        return null;
      })
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i);
  }, [family.tiers, talent?.talentLevelAvailable, talent?.levelsAvailable]);

  const initialTierIndex = useMemo(() => {
    const desiredRaw = String(talent?.talentLevel || talent?.seniority || "");
    const desired = desiredRaw
      ? desiredRaw.replace(/entry/i, "Tier 1").replace(/mid/i, "Tier 2").replace(/senior/i, "Tier 3")
      : "";
    const idx = availableTiers.findIndex((t) => t === desired);
    return idx === -1 ? 0 : idx;
  }, [availableTiers, talent?.talentLevel, talent?.seniority]);

  const [tierIndex, setTierIndex] = useState(initialTierIndex);

  useEffect(() => { setTierIndex(initialTierIndex); }, [initialTierIndex, talent]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // ---------- Non-hook computed values ----------
  const displayTier = availableTiers[tierIndex] ?? "Tier 1"; // "Tier 1"|"Tier 2"|"Tier 3"
  const tierObj =
    family.group.find(
      (r) => String(r.talentLevel || "").trim().toLowerCase() === displayTier.toLowerCase()
    ) || talent; // fallback to whatever was passed in

  const avatarsMap =
    (family.avatars && Object.keys(family.avatars).length ? family.avatars : (talent?.avatars || {}));

  const talentKey = `ROLE:${roleName}`;
  const unlocked = isTalentUnlocked(talentKey);

  const prevTier = (e) => {
    e.stopPropagation();
    setTierIndex((i) => (i - 1 + availableTiers.length) % availableTiers.length);
  };
  const nextTier = (e) => {
    e.stopPropagation();
    setTierIndex((i) => (i + 1) % availableTiers.length);
  };

  const handleUnlock = () => {
    setError("");
    if (unlocking || unlocked) return;

    setUnlocking(true);
    const ok = unlockTalent(talentKey, 1); // consume 1 Talent Role Pass
    setUnlocking(false);

    if (!ok) {
      setError("Not enough Talent Role Pass.");
      setShake(true);
      setTimeout(() => setShake(false), 450);
    }
  };

  const j = (arr, sep = ", ") => (Array.isArray(arr) ? arr.join(sep) : (arr || "—"));

  const top = {
    talentId: tierObj?.talentId || "—",
    supportType: tierObj?.supportType || "—",
    supportTypeAvailable: tierObj?.supportTypeAvailable || "—",
    talentLevelsAvailable: availableTiers,
    languages: tierObj?.language,
    functions: tierObj?.function,
    deploymentModel: tierObj?.deploymentModel,
    price: tierObj?.price || "—",
    minimumEngagement: tierObj?.minimumEngagement || "—",
    deploymentTimeline: tierObj?.deploymentTimeline || "—",
  };

  const section = {
    objectives: Array.isArray(tierObj?.roleObjective) ? tierObj.roleObjective : [],
    capabilities: Array.isArray(tierObj?.coreCapabilities) ? tierObj.coreCapabilities : [],
    workflows: Array.isArray(tierObj?.supportedWorkflows) ? tierObj.supportedWorkflows : [],
    deliverables: Array.isArray(tierObj?.keyDeliverables) ? tierObj.keyDeliverables : [],
    tierSummary: Array.isArray(tierObj?.tierSummary) ? tierObj.tierSummary : [],
    typeofSupport:
      tierObj?.typeofSupport && typeof tierObj.typeofSupport === "object"
        ? tierObj.typeofSupport
        : null,
    inclusions: Array.isArray(tierObj?.inclusions) ? tierObj.inclusions : [],
    deploymentEngagement: Array.isArray(tierObj?.deploymentEngagement) ? tierObj.deploymentEngagement : [],
  };

  const bottom = {
    primaryTools: tierObj?.primaryTools,
    fileFormats: tierObj?.fileFormats,
    plugins: tierObj?.Plugins,
    gameType: tierObj?.gameType,
    gameEngine: tierObj?.gameEngine,
    versionControl: tierObj?.versionControl,
    communication: tierObj?.communication,
    workEthic: tierObj?.workEthic,
    hardSkills: tierObj?.hardSkills,
    genre: tierObj?.genre,
    shiftAvailability: tierObj?.shiftAvailability,
    workScheduleFlexibility: tierObj?.workScheduleFlexibility,
  };

  const handleHireNow = () => {
    const params = new URLSearchParams({ role: roleName, tier: displayTier }).toString();
    navigate(`/talent-pool?${params}`, {
      state: { presetFilters: { role: roleName, tier: displayTier }, via: "hire-now" },
    });
  };

  // ---------- Type of Support (no hooks; compute safely every render) ----------
  const preferredChannelOrder = ["Voice", "Call", "Chat", "Email", "Ticket", "In-App", "Social", "Forum", "On-Site"];

  const supportChannels = (() => {
    if (!section.typeofSupport) return [];
    const keys = Object.keys(section.typeofSupport || {});
    return keys.sort((a, b) => {
      const ia = preferredChannelOrder.indexOf(a);
      const ib = preferredChannelOrder.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      return a.localeCompare(b);
    });
  })();

  const supportRows = (() => {
    if (!section.typeofSupport || supportChannels.length === 0) return [];
    const maxLen = Math.max(
      1,
      ...supportChannels.map((c) =>
        Array.isArray(section.typeofSupport[c]) ? section.typeofSupport[c].length : 0
      )
    );
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      rows.push(
        supportChannels.map((c) => {
          const arr = Array.isArray(section.typeofSupport[c]) ? section.typeofSupport[c] : [];
          const v = arr[i];
          const s = (v == null ? "" : String(v)).trim();
          return s || "—";
        })
      );
    }
    return rows;
  })();

  // ✅ Early return AFTER all hooks are declared (safe)
  if (!talent || !roleName) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      {/* OUTSIDE ARROWS now cycle TIERS */}
      <button className="modal-arrow left" onClick={prevTier} aria-label="Previous tier">←</button>
      <button className="modal-arrow right" onClick={nextTier} aria-label="Next tier">→</button>

      {/* Modal panel */}
      <div
        className={`modal-content ${!unlocked ? "blurred" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">✖</button>

        <h1>{roleName}</h1>

        {!unlocked && (
          <div className="modal-lock-overlay" aria-hidden="true">
            <div className={`unlock-wrap ${shake ? "shake" : ""}`}>
              <button
                className="unlock-btn"
                onClick={handleUnlock}
                disabled={unlocking || talentRolePass < 1}
              >
                {unlocking ? "Unlocking..." : `🔓 Unlock Role — Use 1 Pass (${talentRolePass} left)`}
              </button>

              {talentRolePass < 1 && (
                <>
                  <br />
                  <Link
                    className="add-credits-btn"
                    to={{
                      pathname: "/profile",
                      search: "?tab=subscription",
                      state: { openTab: "subscription", via: "header-role-pass" },
                    }}
                    onClick={onClose}
                    aria-label="Add Passes"
                  >
                    ➕ Add Passes
                  </Link>
                </>
              )}

              {error && <div className="unlock-error">{error}</div>}
            </div>
          </div>
        )}

        <div className="modal-grid">
          {/* Left side - Avatar */}
          <div className="modal-left">
            <img
              key={displayTier}
              src={avatarsMap?.[displayTier] || talent?.avatar}
              alt={`${roleName} ${displayTier} avatar`}
              className="modal-avatar-large"
            />
          </div>

          {/* Right side - Info Table */}
          <div className="modal-right">
            <table className="talent-table">
              <tbody>
                <tr><th>Talent ID</th><td>{top.talentId}</td></tr>
                <tr><th>Support Type</th><td>{top.supportType}</td></tr>
                <tr><th>Support Type Available</th><td>{top.supportTypeAvailable}</td></tr>
                <tr><th>Talent Level</th><td>{displayTier}</td></tr>
                <tr><th>Talent Levels Available</th><td>{(availableTiers || []).join(", ")}</td></tr>
                <tr><th>Language</th><td>{j(top.languages)}</td></tr>
                <tr><th>Function / Team</th><td>{j(top.functions)}</td></tr>
                <tr><th>Deployment Model</th><td>{j(top.deploymentModel)}</td></tr>
                <tr><th>Price</th><td>{top.price}</td></tr>
                <tr><th>Minimum Engagement</th><td>{top.minimumEngagement}</td></tr>
                <tr><th>Deployment Timeline</th><td>{top.deploymentTimeline}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 3-column Details swap with tier */}
        <div className="modal-bottom">
          <table className="talent-details-table">
            <thead>
              <tr>
                <th>Role Objective</th>
                <th>Core Capabilities</th>
                <th>Supported Workflows</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <ul>
                    {(section.objectives?.length ? section.objectives : ["—"]).map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </td>
                <td>
                  <ul>
                    {(section.capabilities?.length ? section.capabilities : ["—"]).map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </td>
                <td>
                  <ul>
                    {(section.workflows?.length ? section.workflows : ["—"]).map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Key Deliverables */}
        <div className="modal-tier-summary">
          <table className="tier-summary-table">
            <thead>
              <tr><th>Key Deliverables</th></tr>
            </thead>
            <tbody>
              {(section.deliverables || []).map((row, k) => (
                <tr key={k}><td>{row}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Tier Summary */}
        <div className="modal-tier-summary">
          <h3>Tier Summary</h3>
          <table className="tier-summary-table">
            <thead>
              <tr><th>{displayTier}</th></tr>
            </thead>
            <tbody>
              {(section.tierSummary || []).map((row, k) => (
                <tr key={k}><td>{row}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Type of Support: columns = channels, rows = details */}
        {supportChannels.length > 0 && (
          <div className="modal-tier-summary">
            <h3>Type of Support</h3>
            <table className="tier-summary-table">
              <thead>
                <tr>
                  {supportChannels.map((ch) => (
                    <th key={ch}>{ch}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {supportRows.map((cells, rIdx) => (
                  <tr key={`row-${rIdx}`}>
                    {cells.map((cell, cIdx) => (
                      <td key={`cell-${rIdx}-${cIdx}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Bottom tables powered by role.js */}
        <div className="modal-bottom">
          <table className="talent-details-table">
            <thead>
              <tr>
                <th>Primary Tools / Software</th>
                <th>File Formats Handled</th>
                <th>Plugins</th>
                <th>Game Type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><ul>{j(bottom.primaryTools).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.fileFormats).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.plugins).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.gameType).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
              </tr>
            </tbody>

            <thead>
              <tr>
                <th>Game Engines / Integration</th>
                <th>Version Control & Collaboration</th>
                <th>Communication</th>
                <th>Work Ethic / Traits</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><ul>{j(bottom.gameEngine).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.versionControl).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.communication).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.workEthic).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
              </tr>
            </tbody>

            <thead>
              <tr>
                <th>Hard Skills</th>
                <th>Genre</th>
                <th>Shift Availability</th>
                <th>Work Schedule Flexibility</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><ul>{j(bottom.hardSkills).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td><ul>{j(bottom.genre).split(", ").map((x, i) => <li key={i}>{x}</li>)}</ul></td>
                <td>{j(bottom.shiftAvailability)}</td>
                <td>{j(bottom.workScheduleFlexibility)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Inclusions + Deployment & Engagement Terms (two-column) */}
        <div className="modal-two-col">
          <div className="col">
            <h3>Inclusions</h3>
            <ul>
              {(section.inclusions.length ? section.inclusions : ["—"]).map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>

          <div className="col">
            <h3>Deployment &amp; Engagement Terms</h3>
            <ul>
              {(section.deploymentEngagement.length ? section.deploymentEngagement : ["—"]).map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        </div>

        <button className="btn-hire-now" onClick={handleHireNow}>Hire Now!</button>
      </div>
    </div>
  );
}
